import React, { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import {
  CompactFieldProvider,
  COMPACT_TEXT_VALUE,
  COMPACT_DROPDOWN_VALUE,
} from "@adsk/acc-issues-common/components/primitive/InlineEditCompact";
import type { CompactFieldValue } from "@adsk/acc-issues-common/components/primitive/InlineEditCompact";

export type IssueFieldPresentation = {
  testIdPrefix: string;
};

const DEFAULT_ISSUE_FIELD_PRESENTATION: IssueFieldPresentation = {
  testIdPrefix: "",
};

const IssueFieldPresentationContext = createContext<IssueFieldPresentation>(
  DEFAULT_ISSUE_FIELD_PRESENTATION
);

export function IssueFieldPresentationProvider({
  value,
  children,
}: {
  value: Partial<IssueFieldPresentation>;
  children: ReactNode;
}) {
  const merged = useMemo(
    (): IssueFieldPresentation => ({
      ...DEFAULT_ISSUE_FIELD_PRESENTATION,
      ...value,
    }),
    [value]
  );
  return (
    <IssueFieldPresentationContext.Provider value={merged}>
      {children}
    </IssueFieldPresentationContext.Provider>
  );
}

function useIssueFieldPresentation(): IssueFieldPresentation {
  return useContext(IssueFieldPresentationContext);
}

/**
 * Returns the given base test ID prefixed by the current presentation context's
 * `testIdPrefix`. In the detail panel the prefix is empty so the ID is unchanged;
 * in the table inline-edit cells the prefix is "inline-", yielding e.g. "inline-title-field".
 */
export function useFieldTestId(baseTestId: string): string {
  const { testIdPrefix } = useIssueFieldPresentation();
  return `${testIdPrefix}${baseTestId}`;
}

const INLINE_EDIT_PRESENTATION: Partial<IssueFieldPresentation> = {
  testIdPrefix: "inline-",
};

export function InlineEditPresentationProvider({
  value,
  children,
}: {
  value: CompactFieldValue;
  children: ReactNode;
}) {
  return (
    <CompactFieldProvider value={value}>
      <IssueFieldPresentationProvider value={INLINE_EDIT_PRESENTATION}>
        {children}
      </IssueFieldPresentationProvider>
    </CompactFieldProvider>
  );
}

export function InlineEditTextPresentationProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <InlineEditPresentationProvider value={COMPACT_TEXT_VALUE}>
      {children}
    </InlineEditPresentationProvider>
  );
}

export function InlineEditDropdownPresentationProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <InlineEditPresentationProvider value={COMPACT_DROPDOWN_VALUE}>
      {children}
    </InlineEditPresentationProvider>
  );
}
